import { getItemAsync, storeItemAsync } from "@towersystems/roam-common-native";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import {
  QuaggaScannerSettingsItemSchema,
  barcodeSettingsStorageKey,
  defaultReaders,
  yupObjectOf,
} from "../../../../utilities/barcode-scanner/constants";
import { BarcodeSettingsContext } from "../../../../utilities/barcode-scanner/context";
import { ReaderData } from "../../../../utilities/barcode-scanner/types";

export interface BarcodeSettingsProviderProps {
  children?: React.ReactNode;
}

export function BarcodeSettingsProvider({
  children,
}: BarcodeSettingsProviderProps) {
  const [readers, setReaders] = useState<ReaderData>(defaultReaders);
  const [bootstrapping, setBootstrapping] = useState<boolean>(true);

  async function updateReaders(newReaders: ReaderData) {
    setReaders(newReaders);
  }

  async function save(readersToSave: ReaderData) {
    return storeItemAsync(
      barcodeSettingsStorageKey,
      JSON.stringify(readersToSave)
    );
  }

  async function load() {
    const loadedData = await getItemAsync(barcodeSettingsStorageKey);

    if (!loadedData) return;

    const validatedData = Yup.object()
      .test(yupObjectOf(QuaggaScannerSettingsItemSchema))
      .validate(JSON.parse(loadedData)) as unknown as ReaderData;

    return validatedData;
  }

  function handleLoadFailed() {
    setReaders(defaultReaders);
  }

  async function reset() {
    return save(defaultReaders)
      .then(() => {
        updateReaders(defaultReaders);
      })
      .catch(() => undefined);
  }

  useEffect(() => {
    setBootstrapping(true);
    load()
      .then((newReaders) => {
        setReaders(newReaders || defaultReaders);
      })
      .catch(handleLoadFailed)
      .finally(() => {
        setBootstrapping(false);
      });
  }, []);

  return (
    <BarcodeSettingsContext.Provider
      value={{
        readers,
        updateReaders,
        save,
        reset,
        bootstrapping,
      }}
    >
      {children}
    </BarcodeSettingsContext.Provider>
  );
}
